import {
  TEMPLATE_KEYWORD_CLASSIFY_BATCH_SIZE,
  type TemplateKeywordCategory,
  type TemplateKeywordGeneratePool,
} from "@adpilot/shared";
import { IconArrowRight, IconPlus, IconSearch, IconSparkles } from "@tabler/icons-react";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTab } from "@/components/ui/tabs";
import { getApiErrorMessage } from "@/lib/api-error";
import { cn } from "@/lib/utils";
import { useAuthStore } from "@/stores/auth";
import {
  templatePortfolioKeys,
  useStartTemplateKeywordClassify,
  useStartTemplateKeywordGenerate,
  useTemplateKeywordClassifyTask,
  useTemplateKeywordGenerateTasks,
} from "./api";
import { AddKeywordsDialog, ClassifyDialog, GenerateDialog } from "./keywords/dialogs";
import { type ClassifyProgress, ImportPanel } from "./keywords/ImportPanel";
import {
  PoolEmptyState,
  SearchEmptyState,
  StartEmptyState,
  UnclassifiedEmptyState,
} from "./keywords/KeywordEmptyState";
import { KeywordTable, KeywordTableSkeleton } from "./keywords/KeywordTable";
import {
  countCampaigns,
  estimateClassifyMinutes,
  isGeneratePool,
  KEYWORD_POOLS,
  type KeywordPool,
  manualKeyword,
  POSITIVE_POOLS,
  parseManualKeywords,
  sortPool,
} from "./keywords/model";
import { useExcelImport } from "./keywords/use-excel-import";
import { useKeywordEdits } from "./keywords/use-keyword-edits";
import {
  type TemplatePortfolioDraftStepProps,
  TemplatePortfolioFooter,
} from "./TemplatePortfolioShell";

export interface StepKeywordsProps extends TemplatePortfolioDraftStepProps {
  /** Called once the keyword pools are saved; the shell moves to ③ 结构预览. */
  onNext: () => void;
}

const ACTIVE = new Set(["queued", "running"]);
const nf = (value: number) => value.toLocaleString("en-US");

/** ② 关键词 (docs/tech/12 §3 ②, Paper TP-02~08). */
export function StepKeywords({ shopId, draft, onBack, onNext }: StepKeywordsProps) {
  const { t } = useTranslation("templatePortfolio");
  const queryClient = useQueryClient();
  const { keywords, edit, flush, latestDraft } = useKeywordEdits(shopId, draft);
  const prepare = useCallback(async () => {
    await flush();
    return latestDraft().version;
  }, [flush, latestDraft]);
  const excel = useExcelImport(shopId, draft.id, prepare);
  const fileInput = useRef<HTMLInputElement>(null);

  const [pool, setPool] = useState<KeywordPool>("core");
  const [search, setSearch] = useState("");
  const [classifyOpen, setClassifyOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [generatePool, setGeneratePool] = useState<TemplateKeywordGeneratePool | null>(null);
  const [leaving, setLeaving] = useState(false);

  const classifyTask = useTemplateKeywordClassifyTask(shopId, draft.id).data;
  const generateTasks = useTemplateKeywordGenerateTasks(shopId, draft.id).data;
  const startClassify = useStartTemplateKeywordClassify(shopId, draft.id);
  const startGenerate = useStartTemplateKeywordGenerate(shopId, draft.id);

  const refetchDraft = useCallback(
    () =>
      queryClient.invalidateQueries({
        queryKey: templatePortfolioKeys.draft(draft.id),
        exact: true,
      }),
    [queryClient, draft.id],
  );

  // AI tasks write the draft server-side; reload it when one finishes.
  const previousClassify = useRef(classifyTask?.status);
  useEffect(() => {
    const before = previousClassify.current;
    previousClassify.current = classifyTask?.status;
    if (!classifyTask || !before || !ACTIVE.has(before) || ACTIVE.has(classifyTask.status)) return;
    void refetchDraft();
    if (classifyTask.status === "completed") {
      toast.success(t("keywords.classify.done", { value: nf(classifyTask.keywordsClassified) }));
    }
  }, [classifyTask, refetchDraft, t]);

  const previousGenerate = useRef(generateTasks);
  useEffect(() => {
    const before = previousGenerate.current;
    previousGenerate.current = generateTasks;
    if (!generateTasks || !before) return;
    for (const task of Object.values(generateTasks)) {
      if (!ACTIVE.has(before[task.pool].status) || ACTIVE.has(task.status)) continue;
      void refetchDraft();
      const poolName = t(`keywords.pools.${task.pool}`);
      if (task.status === "completed") {
        toast.success(t("keywords.generate.done", { count: task.generated, pool: poolName }));
      } else {
        toast.error(t("keywords.generate.failed", { pool: poolName }));
      }
    }
  }, [generateTasks, refetchDraft, t]);

  const unclassified = keywords.filter((keyword) => keyword.category === "unclassified").length;
  const hasClassified = keywords.length > unclassified;
  const classifyActive = ACTIVE.has(classifyTask?.status ?? "idle");
  const classifyFailed = classifyActive
    ? Math.min(
        unclassified,
        (classifyTask?.batchesFailed ?? 0) * TEMPLATE_KEYWORD_CLASSIFY_BATCH_SIZE,
      )
    : classifyTask?.status === "partial" || classifyTask?.status === "failed"
      ? unclassified
      : 0;
  const progress: ClassifyProgress = {
    active: classifyActive,
    done: Math.min(classifyTask?.keywordsClassified ?? 0, unclassified),
    total: unclassified,
    remainingMinutes: estimateClassifyMinutes(
      (classifyTask?.batchesTotal ?? 0) - (classifyTask?.batchesDone ?? 0),
    ),
    failed: classifyFailed,
  };
  const variant =
    keywords.length === 0 && draft.importedFiles.length === 0 && excel.pending.length === 0
      ? "empty"
      : unclassified > 0 || classifyActive || excel.pending.length > 0
        ? "files"
        : "compact";

  const poolKeywords = useMemo(
    () => sortPool(keywords.filter((keyword) => keyword.category === pool)),
    [keywords, pool],
  );
  const query = search.trim().toLowerCase();
  const rows = query
    ? poolKeywords.filter((keyword) => keyword.text.toLowerCase().includes(query))
    : poolKeywords;
  const counts = useMemo(() => {
    const result = Object.fromEntries(KEYWORD_POOLS.map((key) => [key, 0])) as Record<
      KeywordPool,
      number
    >;
    let negativeSelected = 0;
    for (const keyword of keywords) {
      if (keyword.category === "unclassified") continue;
      result[keyword.category] += 1;
      if (keyword.category === "negative" && keyword.selected) negativeSelected += 1;
    }
    return { ...result, negativeSelected };
  }, [keywords]);

  const canNext =
    !classifyActive &&
    keywords.some((keyword) => keyword.selected && POSITIVE_POOLS.has(keyword.category));

  const runClassify = async (retry: boolean) => {
    try {
      await flush();
      await startClassify.mutateAsync({ retry });
      setClassifyOpen(false);
    } catch (error) {
      toast.error(await getApiErrorMessage(error));
    }
  };

  const runGenerate = async (target: TemplateKeywordGeneratePool, count: number) => {
    try {
      await flush();
      await startGenerate.mutateAsync({ pool: target, count });
      setGeneratePool(null);
    } catch (error) {
      toast.error(await getApiErrorMessage(error));
    }
  };

  const addKeywords = (input: string, category: TemplateKeywordCategory) => {
    const texts = parseManualKeywords(input, keywords);
    setAddOpen(false);
    if (texts.length === 0) return;
    edit({ type: "add", keywords: texts.map((text) => manualKeyword(text, category)) });
    if (category !== "unclassified") setPool(category as KeywordPool);
    toast.success(t("keywords.add.done", { count: texts.length }));
  };

  const goNext = async () => {
    setLeaving(true);
    try {
      await flush();
      onNext();
    } catch (error) {
      toast.error(await getApiErrorMessage(error));
    } finally {
      setLeaving(false);
    }
  };

  // A demo deployment has no keyword model: it classifies by rules but cannot generate.
  const canGenerate = useAuthStore((state) => state.user?.deploymentMode !== "demo");
  const generating = (target: KeywordPool) =>
    isGeneratePool(target) && ACTIVE.has(generateTasks?.[target].status ?? "idle");

  const openGenerate = () => {
    if (isGeneratePool(pool)) setGeneratePool(pool);
  };

  let content: React.ReactNode;
  if (variant === "empty") {
    content = (
      <StartEmptyState onUpload={() => fileInput.current?.click()} onAdd={() => setAddOpen(true)} />
    );
  } else if (!hasClassified && poolKeywords.length === 0) {
    content = classifyActive ? (
      <KeywordTableSkeleton />
    ) : (
      <UnclassifiedEmptyState
        onClassify={() => setClassifyOpen(true)}
        onAdd={() => setAddOpen(true)}
      />
    );
  } else {
    content = (
      <>
        <div className="flex items-center justify-end gap-4 py-3">
          <div className="flex shrink-0 items-center gap-2">
            <label className="flex h-8 w-[200px] shrink-0 items-center gap-2 rounded-lg border border-control-border px-2.5 focus-within:border-ring">
              <IconSearch className="size-3.5 shrink-0 text-muted-foreground" aria-hidden />
              <input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder={t("keywords.toolbar.search")}
                className="min-w-0 flex-1 bg-transparent text-xs text-ink outline-none placeholder:text-placeholder"
              />
            </label>
            <Button
              variant="outline"
              size="sm"
              className="h-8 rounded-lg border-line bg-surface px-3 text-xs text-ink"
              onClick={() => setAddOpen(true)}
            >
              <IconPlus className="size-3.5" aria-hidden />
              {t("keywords.toolbar.add")}
            </Button>
            {isGeneratePool(pool) && canGenerate ? (
              <Button
                variant="outline"
                size="sm"
                loading={generating(pool)}
                className="h-8 rounded-lg border-badge-information-border bg-badge-information-background px-3 text-xs text-badge-information-foreground hover:bg-badge-information-background/80"
                onClick={openGenerate}
              >
                <IconSparkles className="size-3.5" aria-hidden />
                {t("keywords.toolbar.generate")}
              </Button>
            ) : null}
          </div>
        </div>
        {poolKeywords.length === 0 ? (
          <PoolEmptyState
            pool={pool}
            onGenerate={canGenerate ? openGenerate : undefined}
            onAdd={() => setAddOpen(true)}
          />
        ) : rows.length === 0 ? (
          <SearchEmptyState query={search.trim()} />
        ) : (
          <KeywordTable
            key={pool}
            pool={pool}
            rows={rows}
            onSelect={(keys, selected) => edit({ type: "selected", keys, selected })}
            onMove={(keys, category) => edit({ type: "category", keys, category })}
          />
        )}
      </>
    );
  }

  return (
    <>
      <input
        ref={fileInput}
        type="file"
        multiple
        accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        className="hidden"
        onChange={(event) => {
          const files = [...(event.target.files ?? [])];
          event.target.value = "";
          if (files.length > 0) void excel.addFiles(files);
        }}
      />
      <div className={cn("flex flex-col pb-6", variant === "files" && "gap-4")}>
        <ImportPanel
          variant={variant}
          draft={draft}
          keywordCount={keywords.length}
          allClassified={unclassified === 0 && hasClassified}
          excel={excel}
          onPick={() => fileInput.current?.click()}
          classify={progress}
          onClassify={() => setClassifyOpen(true)}
          onRetry={() => void runClassify(true)}
          retrying={startClassify.isPending}
        />
        <div
          className={cn(
            "flex items-end justify-between border-b border-line",
            variant !== "files" && "pt-4",
          )}
        >
          <Tabs value={pool} onValueChange={(value) => setPool(value as KeywordPool)}>
            <TabsList className="items-end gap-5 border-b-0 [&>[data-slot=tab-indicator]]:bg-ink">
              {KEYWORD_POOLS.map((key) => {
                const active = key === pool;
                const negative = key === "negative" && counts.negative > 0;
                return (
                  <TabsTab
                    key={key}
                    value={key}
                    className={cn(
                      "h-10 gap-1.5 border-0 px-0 text-[13px] font-normal leading-[18px] text-text-secondary data-active:font-semibold data-active:text-ink",
                      key === "discard" && "border-l border-line pl-4 text-muted-foreground",
                    )}
                  >
                    {t(`keywords.pools.${key}`)}
                    <span
                      className={cn(
                        "rounded-full px-1.5 py-px text-[11px] font-medium leading-[14px]",
                        negative
                          ? "bg-badge-warning-background text-badge-warning-foreground"
                          : active
                            ? "bg-ink text-surface"
                            : "bg-subtle",
                      )}
                    >
                      {negative ? `${counts.negativeSelected}/${counts.negative}` : nf(counts[key])}
                    </span>
                  </TabsTab>
                );
              })}
            </TabsList>
          </Tabs>
          {variant === "empty" ? (
            <Button disabled className="h-8 self-start rounded-lg px-3 text-xs">
              <IconSparkles className="size-3.5" aria-hidden />
              {t("keywords.classify.action")}
            </Button>
          ) : hasClassified ? (
            <div className="flex h-10 items-center gap-1.5">
              <span className="text-xs text-muted-foreground">{t("keywords.campaigns.label")}</span>
              <span className="text-[13px] font-semibold leading-[18px] text-ink">
                {t("keywords.campaigns.value", { count: countCampaigns(keywords) })}
              </span>
            </div>
          ) : null}
        </div>
        {content}
      </div>
      <TemplatePortfolioFooter>
        <Button
          variant="outline"
          className="h-9 border-line bg-surface px-3 text-xs text-ink"
          onClick={onBack}
        >
          {t("actions.back")}
        </Button>
        <Button
          className="h-9 rounded-lg px-3 text-xs"
          disabled={!canNext}
          loading={leaving}
          onClick={() => void goNext()}
        >
          {t("keywords.next")}
          <IconArrowRight className="size-3.5" aria-hidden />
        </Button>
      </TemplatePortfolioFooter>
      <ClassifyDialog
        open={classifyOpen}
        onOpenChange={setClassifyOpen}
        pendingCount={unclassified}
        starting={startClassify.isPending}
        onConfirm={() => void runClassify(false)}
      />
      <AddKeywordsDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        defaultCategory={pool}
        onAdd={addKeywords}
      />
      <GenerateDialog
        pool={generatePool}
        onOpenChange={(open) => !open && setGeneratePool(null)}
        shopId={shopId}
        sku={draft.settings.product?.sku ?? null}
        keywords={keywords}
        starting={startGenerate.isPending}
        onConfirm={(target, count) => void runGenerate(target, count)}
      />
    </>
  );
}
